import { useEffect, useState } from "react";
import api from "../../../lib/api";
import { card, btn, btnGhost, label } from "./_genCommon";
import DataTabView from "./DataTabView";
import Scene3DViewer from "./Scene3DViewer";

/** Modal full-design viewer with tabs (PFD, Floor Plan, Equipment List,
 *  Utilities, Cost Breakdown, 3D Model, Alignment, Profile, Cross Section,
 *  Earthwork, Structure Schedule, BOQ, etc.) — view kind drives the renderer. */
export default function AlternativeFullView({ projectId, alt, onClose,
                                                            onAdopt, isAdopted }) {
  const [tab, setTab] = useState(null);
  const [tabKind, setTabKind] = useState("svg");
  const [views, setViews] = useState([]);
  const [svgs, setSvgs] = useState({});
  const [tables, setTables] = useState({});
  const [scenes, setScenes] = useState({});
  const [floorIdx, setFloorIdx] = useState(0);
  const [side, setSide] = useState("north");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!alt) return;
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/generative/views?domain=${alt.domain}`);
        const list = (r.data?.views || []).filter(v => v.key !== "thumbnail");
        setViews(list);
        if (!tab && list.length) {
          setTab(list[0].key);
          setTabKind(list[0].kind || "svg");
        }
      } catch (e) { setError(String(e)); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alt?.id]);

  // Lazy-load content per (tab, options)
  useEffect(() => {
    if (!alt || !tab) return;
    const meta = views.find(v => v.key === tab);
    const kind = meta?.kind || "svg";
    setTabKind(kind);
    const cacheKey = `${tab}::${floorIdx}::${side}`;
    if (kind === "svg") {
      if (svgs[cacheKey]) return;
      const params = new URLSearchParams();
      if (tab === "floor-plan") params.set("floor", floorIdx);
      if (tab === "elevation") params.set("side", side);
      const url = `/projects/${projectId}/generative/alternatives/${alt.id}/view/${tab}.svg`
                    + (params.toString() ? `?${params.toString()}` : "");
      api.get(url)
          .then(r => setSvgs(prev => ({ ...prev, [cacheKey]: r.data?.svg || "" })))
          .catch(e => setError(String(e)));
    } else if (kind === "table") {
      if (tables[tab]) return;
      api.get(`/projects/${projectId}/generative/alternatives/${alt.id}/data/${tab}`)
          .then(r => setTables(prev => ({ ...prev, [tab]: r.data })))
          .catch(e => setError(String(e)));
    } else if (kind === "scene") {
      if (scenes[tab]) return;
      api.get(`/projects/${projectId}/generative/alternatives/${alt.id}/scene-3d.json`)
          .then(r => setScenes(prev => ({ ...prev, [tab]: r.data })))
          .catch(e => setError(String(e)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, floorIdx, side, alt?.id, views.length]);

  if (!alt) return null;
  const cacheKey = `${tab}::${floorIdx}::${side}`;
  const totalFloors = alt.areas?.floors || 1;

  const renderBody = () => {
    if (tabKind === "table") return <DataTabView data={tables[tab]} />;
    if (tabKind === "scene")
      return <Scene3DViewer scene={scenes[tab]} />;
    const cur = svgs[cacheKey];
    return cur
        ? <div style={{ width: "100%" }} dangerouslySetInnerHTML={{ __html: cur }} />
        : <div style={{ padding: 80, textAlign: "center",
                              color: "var(--pf-muted)", fontSize: 12 }}>Loading {tab}…</div>;
  };

  return (
    <div data-testid="gen-full-view" style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)",
        zIndex: 1000, display: "flex", flexDirection: "column",
        padding: 16,
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", marginBottom: 8,
                        background: "#0F131C", border: "1px solid var(--pf-border)",
                        borderRadius: 6, padding: "10px 14px" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#F26B1F" }}>
            {alt.id} — {alt.strategy_name || "Variant"}
          </div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            CAPEX {alt.cost?.capex_INR_cr} cr · OPEX {alt.cost?.opex_INR_cr_per_year} cr/yr ·
            {" "}{alt.schedule?.months} mo · Score{" "}
            <b style={{ color: "#F26B1F" }}>{alt.scores?.weighted_total ?? "—"}/100</b>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {onAdopt && (
            <button data-testid="gen-full-adopt" style={btn} onClick={() => onAdopt(alt.id)}>
              {isAdopted ? "✓ Adopted as base design" : `Select ${alt.id} → Project base`}
            </button>
          )}
          <button data-testid="gen-full-close" style={btnGhost} onClick={onClose}>
            ✕ Close
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
        {views.map(v => (
          <button key={v.key} data-testid={`gen-full-tab-${v.key}`}
              onClick={() => { setTab(v.key); setTabKind(v.kind || "svg"); }}
              style={{ ...btnGhost,
                          borderColor: tab === v.key ? "#F26B1F" : "var(--pf-border)",
                          color: tab === v.key ? "#F26B1F" : "var(--pf-text)",
                          padding: "6px 12px", fontSize: 12 }}>
            {v.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {tab === "floor-plan" && (
          <select value={floorIdx} onChange={(e) => setFloorIdx(parseInt(e.target.value))}
              data-testid="gen-full-floor"
              style={{ ...btnGhost, fontSize: 11, padding: "4px 8px" }}>
            {Array.from({ length: totalFloors }, (_, i) => (
              <option key={i} value={i}>Floor {i === 0 ? "GF" : i}</option>
            ))}
          </select>
        )}
        {tab === "elevation" && (
          <select value={side} onChange={(e) => setSide(e.target.value)}
              data-testid="gen-full-side"
              style={{ ...btnGhost, fontSize: 11, padding: "4px 8px" }}>
            {["north", "south", "east", "west"].map(s =>
                <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>
        )}
      </div>

      {/* Body — split: viewer + side info */}
      <div style={{ flex: 1, display: "grid",
                        gridTemplateColumns: "1fr 320px", gap: 12, minHeight: 0 }}>
        <div style={{ ...card, padding: 8, overflow: "auto",
                          background: "#0E1116" }}>
          {renderBody()}
        </div>
        <div style={{ ...card, padding: 12, overflow: "auto" }}>
          <div style={label}>At-a-glance</div>
          <div style={{ marginTop: 6, fontSize: 11 }}>
            {alt.domain === "process_plant" && (
              <ul style={{ paddingLeft: 16 }}>
                <li>Columns: <b>{alt.equipment?.columns}</b></li>
                <li>Pumps: <b>{alt.equipment?.pumps}</b></li>
                <li>Heat exchangers: <b>{alt.equipment?.heat_exchangers}</b></li>
                <li>Vessels: <b>{alt.equipment?.vessels}</b></li>
                <li>Compressors: <b>{alt.equipment?.compressors}</b></li>
                <li>Process lines: <b>{alt.line_count}</b></li>
                <li>Instruments: <b>{alt.instrument_count}</b></li>
                <li>Plot area: <b>{alt.plot_area_m2} m²</b></li>
              </ul>
            )}
            {alt.domain === "real_estate" && (
              <ul style={{ paddingLeft: 16 }}>
                <li>Form: <b>{alt.variations?.form?.type}</b></li>
                <li>Floors: <b>{alt.areas?.floors}</b></li>
                <li>Footprint: <b>{alt.areas?.footprint_m2} m²</b></li>
                <li>BUA: <b>{alt.areas?.BUA_m2} m²</b></li>
                <li>Carpet: <b>{alt.areas?.carpet_m2} m²</b></li>
                <li>Units: <b>{alt.areas?.num_units}</b></li>
                <li>Ground coverage: <b>{alt.areas?.ground_coverage_pct} %</b></li>
              </ul>
            )}
            {alt.domain === "infrastructure" && (
              <ul style={{ paddingLeft: 16 }}>
                <li>Length: <b>{alt.alignment?.length_km} km</b></li>
                <li>Lanes: <b>{alt.alignment?.lanes}</b></li>
                <li>Pavement: <b>{alt.pavement?.type}</b></li>
                <li>Median: <b>{alt.median?.type}</b></li>
                <li>Bridges: <b>{(alt.bridges || []).length}</b></li>
                <li>Culverts: <b>{((alt.structures || []).filter(s => s.category === "Culvert")).length}</b></li>
                <li>Cut: <b>{alt.earthwork?.cut_m3} m³</b></li>
                <li>Fill: <b>{alt.earthwork?.fill_m3} m³</b></li>
                <li>Balance: <b>{alt.earthwork?.balance_pct} %</b></li>
              </ul>
            )}
          </div>
          <div style={{ ...label, marginTop: 12 }}>Scores</div>
          <div style={{ marginTop: 6, fontSize: 11, display: "grid",
                              gridTemplateColumns: "1fr auto", gap: 4 }}>
            {Object.entries(alt.scores || {}).map(([k, v]) => (
              <div key={k} style={{ display: "contents" }}>
                <span style={{ color: "var(--pf-muted)" }}>{k.replace(/_/g, " ")}</span>
                <b style={{ color: k === "weighted_total" ? "#F26B1F" : "var(--pf-text)" }}>
                  {typeof v === "number" ? v.toFixed(0) : v}
                </b>
              </div>
            ))}
          </div>
          {alt.pros?.length > 0 && <>
            <div style={{ ...label, marginTop: 12, color: "#22c55e" }}>Pros</div>
            {alt.pros.map((p, i) => (
              <div key={i} style={{ fontSize: 11, marginTop: 2 }}>✓ {p}</div>
            ))}
          </>}
          {alt.cons?.length > 0 && <>
            <div style={{ ...label, marginTop: 8, color: "#ef4444" }}>Cons</div>
            {alt.cons.map((p, i) => (
              <div key={i} style={{ fontSize: 11, marginTop: 2 }}>· {p}</div>
            ))}
          </>}
          {error && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 10 }}>
            {error}
          </div>}
        </div>
      </div>
    </div>
  );
}
